import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaRegCircleQuestion } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { array, InferType, object } from 'yup';

import {
  AgreementSpaceReadModel,
  PermissionKeys,
  SpaceReadModel,
  TenantErpIntegrationReadModel,
  useGetAgreementsByIdQuery,
  usePutAgreementsByAgreementIdSpacesMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  FormSubmitButton,
  InfoTooltip,
  isDefinedString,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  useAreaMetricString,
  useFormAdd,
  useTotalAmountString,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { AgreementSpaceItem } from './agreement-space-item.component';
import { AgreementSpaceFormData, AgreementSpaceItemForm, agreementSpaceSchema } from './agreement-space-item.form';

import { CancelRequestModal, SpacesAddModal } from '../../../../../components';
import { AgreementPermissionBasedActionButton } from '../../../../../components/agreement-permission-based-button/agreement-permission-based-button';
import { useTenants } from '../../../../../hooks';

const schema = object({
  spaces: array().of(agreementSpaceSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export type AgreementSpacesFormData = FormValues;

/**
 * Header cell for the derived area columns. These three figures are computed rather than entered,
 * so the tooltip spells out the formula behind each one.
 */
function TableHeadCellWithTooltip({
  className,
  label,
  tooltip,
}: {
  className?: string;
  label: string;
  tooltip: string;
}) {
  return (
    <TableHeadCell className={className}>
      <span className="inline-flex items-center gap-1">
        {label}
        <InfoTooltip message={tooltip} messageClassName="block w-64 z-50" enterDelay="delay-100">
          <FaRegCircleQuestion className="text-gray-400" aria-label={tooltip} />
        </InfoTooltip>
      </span>
    </TableHeadCell>
  );
}

export interface AgreementSpacesCardProps {
  className?: string;
  onLoaded?: (agreementSpaces: AgreementSpaceReadModel[]) => void;
}

export function AgreementSpacesCard({ className, onLoaded }: AgreementSpacesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tAgreements } = useTranslation('agreements');

  const { id } = useParams<{ id: string }>();
  const [buildingId] = useQueryParam('buildingId', StringParam);
  const { selectedTenant } = useTenants();

  // Check if ERP is configured for the current tenant
  const isErpConfigured = selectedTenant && typeof selectedTenant === 'object' && 'erpIntegrations' in selectedTenant
    ? !!selectedTenant.erpIntegrations?.some((erp: TenantErpIntegrationReadModel) => erp.configured)
    : false;

  // const pageSize = 20;
  // const [page, setPage] = useState(0);

  const [editing, setEditing] = useState<boolean>(false);

  const {
    data: agreement,
    isFetching,
    isLoading,
    refetch,
  } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const isAgreementActive = agreement?.status === 'Effective';
  const isTerminated = agreement?.status === 'Terminated';

  const [putAgreementSpaces] = usePutAgreementsByAgreementIdSpacesMutation();

  const agreementSpaces = useMemo(() => agreement?.spaces ?? [], [agreement?.spaces]);
  // const totalCount = agreementSpaces != null && agreementSpaces.length > 0 ? agreementSpaces.length : 0;

  useEffect(() => {
    if (agreementSpaces.length > 0) {
      onLoaded?.(agreementSpaces);
    }
  }, [agreementSpaces]);

  const onSubmit = async (data: FormValues) => {
    if (id == null) return;
    const spaces = data.spaces.map((space) => {
      // Build overwrittenUnitAmount if price is overridden
      const overwrittenUnitAmount = space.overwrittenPrice && space.price?.unitAmount?.amount != null
        ? {
            amount: space.price.unitAmount.amount,
            currency: { code: space.price.unitAmount?.currency?.code ?? 'NOK' },
          }
        : null;

      return {
        id: space.id || null,
        spaceId: space.spaceId,
        priceId: space.priceId,
        overrideArea: space.overrideArea ?? null,
        overwrittenUnitAmount,
      };
    });

    try {
      await putAgreementSpaces({
        agreementId: id,
        body: spaces,
      }).unwrap();
      // Refetch agreement data to get the updated spaces with new prices
      await refetch();
    } catch (e) {
      console.error('Error saving spaces:', e);
      return;
    }

    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      spaces: agreementSpaces.map<AgreementSpaceFormData>((data) => {
        // If there's an overwritten amount, use it; otherwise use the price's unit amount
        const hasOverwrittenPrice = data.overwrittenUnitAmount != null;
        const effectiveAmount = hasOverwrittenPrice 
          ? data.overwrittenUnitAmount?.amount 
          : data.price?.unitAmount?.amount;
        
        return {
          id: data.id ?? undefined,
          spaceId: data.space?.id ?? '',
          priceId: data.price?.id ?? '',
          price: {
            id: data.price?.id ?? '',
            productId: data.price?.product?.id ?? '',
            unitAmount: {
              amount: effectiveAmount ?? ('' as any),
              currency: {
                code: data.price?.unitAmount?.currency?.code ?? '',
              },
            },
            description: data.price?.description ?? '',
            interval: {
              type: data.price?.interval?.type ?? ('' as any),
              count: data.price?.interval?.count ?? ('' as any),
            },
            source: data.price?.source,
          },
          overwrittenPrice: hasOverwrittenPrice,
          overrideArea: data.overrideArea ?? undefined,
        };
      }),
    }),
    [agreementSpaces],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
  } = methods;

  /**
   * #########################################
   * Edit Agreement Spaces
   */

  const [editingAgreementSpacesForm, setEditingAgreementSpacesForm] = useState<Map<number, boolean>>(new Map());

  const editingAgreementSpaces = useMemo(() => editingAgreementSpacesForm.size > 0, [editingAgreementSpacesForm.size]);

  const {
    fields: editedAgreementSpaces,
    items: spaces,
    setItems: setSpaces,
    adding: addingSpaces,
    enableAdding: handleOpenAddSpaces,
    disableAdding: handleCloseAddSpaces,
    setNewItems: handleAddSpaces,
    removeItemByFieldIndex: handleRemoveSpace,
    getItemByFieldIndex: getSpaceByFieldIndex,
    getFormItemByFieldIndex: getAgreementSpaceByFieldIndex,
  } = useFormAdd<AgreementSpaceFormData, SpaceReadModel>({
    control,
    name: 'spaces',
    // mapItemToFormItem: (x): AgreementSpaceFormData => ({ id: undefined, spaceId: x.id, priceId: x.rent?.id || '' }),

    mapItemToFormItem: (x): AgreementSpaceFormData => ({
      spaceId: x.id,
      priceId: x.rent?.id ?? '',
      price:
        (x.rent && {
          productId: x.rent.product.id,
          unitAmount: x.rent.unitAmount,
          description: x.rent.description,
          id: x.rent.id,
          interval: x.rent.interval,
          source: x.rent.source,
        }) ||
        (undefined as any),
    }),
    itemEqualsFormItem: (a, x) => a.id === x.spaceId,
    formItemEqualsFormItem: (x, y) => x.spaceId === y.spaceId,
  });

  useEffect(() => {
    setSpaces(agreementSpaces.map((x) => x.space));
    reset(defaultValues);
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * #########################################
   */

  const hasAgreementSpaces = agreementSpaces != null && agreementSpaces.length <= 0;
  const hasEditedAgreementSpaces = editedAgreementSpaces != null && editedAgreementSpaces.length <= 0;

  const isListEmpty = useMemo(
    () => (!editing ? hasAgreementSpaces : hasEditedAgreementSpaces),
    [editing, hasAgreementSpaces, hasEditedAgreementSpaces],
  );

  const handleOpenEdit = () => {
    setEditing(true);
    // setPage(0);
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  const totalAmount = useTotalAmountString(agreement?.totalSpaceYearlyAmount, agreement?.totalSpaceOneTimeAmount);
  const totalArea = useAreaMetricString(agreement?.totalArea);

  // Get the building ID from the first space to constrain "Legg til arealer" to the same building
  const constrainedBuildingId = useMemo(() => {
    // If spaces exist, use the building ID from the first space
    if (spaces && spaces.length > 0) {
      return spaces[0]?.building?.id;
    }
    // Otherwise, use the buildingId from URL (if navigating from a building)
    return buildingId ?? undefined;
  }, [spaces, buildingId]);
  
  // Calculate total override area
  const totalOverrideAreaValue = useMemo(() => {
    if (!agreementSpaces || agreementSpaces.length === 0) return undefined;
    const sum = agreementSpaces.reduce((acc, space) => acc + (space.overrideArea ?? 0), 0);
    return sum > 0 ? { metric: sum, imperial: 0 } : undefined;
  }, [agreementSpaces]);
  const totalOverrideAreaString = useAreaMetricString(totalOverrideAreaValue);

  // Calculate total common area share and calculated lettable area
  const totalCommonAreaShareValue = useMemo(() => {
    if (!agreementSpaces || agreementSpaces.length === 0) return undefined;
    const sum = agreementSpaces.reduce((acc, space) => acc + (space.commonAreaShare ?? 0), 0);
    return sum > 0 ? { metric: sum, imperial: 0 } : undefined;
  }, [agreementSpaces]);
  const totalCommonAreaShareString = useAreaMetricString(totalCommonAreaShareValue);

  const totalCalculatedLettableAreaValue = useMemo(() => {
    if (!agreementSpaces || agreementSpaces.length === 0) return undefined;
    const sum = agreementSpaces.reduce((acc, space) => acc + (space.calculatedLettableArea ?? 0), 0);
    return sum > 0 ? { metric: sum, imperial: 0 } : undefined;
  }, [agreementSpaces]);
  const totalCalculatedLettableAreaString = useAreaMetricString(totalCalculatedLettableAreaValue);

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="highlighted">
                {tGlobal('entity.agreementSpace', { context: 'plural' })}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <AgreementPermissionBasedActionButton
                    agreementId={id ?? ''}
                    permissions={[PermissionKeys.Agreement.Update]}
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || agreement == null || isTerminated}
                    disabledTooltip={isTerminated ? tAgreements('terminatedAgreementEditDisabled') : undefined}
                    title={tCommon('edit')}
                  />
                ) : (
                  <>
                    <Button color="primary-dark" size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </Button>
                    <Button size="sm" color="primary-dark" disabled={isFetching} onClick={handleOpenAddSpaces}>
                      {tCommon('add')}
                    </Button>
                    <FormSubmitButton
                      color="primary"
                      type="submit"
                      size="sm"
                      disabled={isFetching || editingAgreementSpaces}
                    >
                      {tCommon('save')}
                    </FormSubmitButton>
                  </>
                )}
              </CardActions>
            </CardHeader>

            <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
              <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
                {!isLoading && isListEmpty && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {tCommon('noEntitySpecified', {
                        entity: tGlobal('entity.agreementSpace', { context: 'plural' }).toLowerCase(),
                      })}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-[13%]">{tGlobal('common.name')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementSpace.type')}</TableHeadCell>
                    <TableHeadCell className="w-[7%]">{tGlobal('space.area')}</TableHeadCell>
                    <TableHeadCellWithTooltip
                      className="w-[8%]"
                      label={tGlobal('agreementSpace.commonAreaShare')}
                      tooltip={tGlobal('agreementSpace.commonAreaShareTooltip')}
                    />
                    <TableHeadCellWithTooltip
                      className="w-[9%]"
                      label={tGlobal('agreementSpace.calculatedLettableArea')}
                      tooltip={tGlobal('agreementSpace.calculatedLettableAreaTooltip')}
                    />
                    <TableHeadCellWithTooltip
                      className="w-[9%]"
                      label={tGlobal('agreementSpace.overrideArea')}
                      tooltip={tGlobal('agreementSpace.overrideAreaTooltip')}
                    />
                    <TableHeadCell className="w-[12%]">{tGlobal('agreementSpace.productName')}</TableHeadCell>
                    <TableHeadCell className="w-[10%]">{tGlobal('agreementSpace.productPrice')}</TableHeadCell>
                    <TableHeadCell className="w-[9%]">{tGlobal('agreementSpace.overridePrice')}</TableHeadCell>
                    <TableHeadCell className="w-[7%]">{tGlobal('agreementSpace.isInvoiced')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]" align="right">
                      {tGlobal('agreementSpace.total')}
                    </TableHeadCell>
                  </TableHeadRow>
                </TableHead>

                <TableBody>
                  {isLoading && Array.from(Array(3).keys()).map((x) => <AgreementSpaceItem key={x} isFetching />)}

                  {!isLoading &&
                    !editing &&
                    agreementSpaces?.map((space, index) => (
                      <AgreementSpaceItem key={space.id} agreementSpace={space} isFetching={isFetching} />
                    ))}

                  {!isLoading &&
                    editing &&
                    editedAgreementSpaces?.map((field, index) => {
                      const currentSpace = agreementSpaces.find(s => s.id === methods.getValues(`spaces.${index}.id`));
                      const isReadOnly = isErpConfigured && currentSpace?.isInvoiced;
                      const canRemove = !isReadOnly;
                      
                      return (
                        <AgreementSpaceItemForm
                          key={field.id}
                          fieldIndex={index}
                          agreementSpace={getAgreementSpaceByFieldIndex(index)}
                          space={getSpaceByFieldIndex(index)}
                          allocation={currentSpace}
                          isFetching={isFetching}
                          isInvoiced={currentSpace?.isInvoiced}
                          readOnly={isReadOnly}
                          onRemove={canRemove ? handleRemoveSpace : undefined}
                          onStartEdit={() => {
                            setEditingAgreementSpacesForm((prev) => {
                              return new Map([...prev, [index, true]]);
                            });
                          }}
                          onEndEdit={() => {
                            editingAgreementSpacesForm.delete(index);
                            setEditingAgreementSpacesForm((prev) => {
                              prev.delete(index);
                              return new Map([...prev]);
                            });
                          }}
                        />
                      );
                    })}
                </TableBody>
                {!isLoading && !editing && !isListEmpty && (
                  <tfoot>
                    <tr className="border-double border-t-2 border-border">
                      <th colSpan={2} align="left" className="pl-4 pr-1 pt-2">
                        <div className="text-sm flex-1 font-medium text-white">{tCommon('total')}</div>
                      </th>
                      <th align="left" className="px-1 pt-2">
                        <Text
                          className="text-sm font-medium text-white"
                          isEmpty={!isDefinedString(totalArea)}
                          loading={isFetching}
                        >
                          {totalArea}
                        </Text>
                      </th>
                      <th align="left" className="px-1 pt-2">
                        <Text
                          className="text-sm font-medium text-white"
                          isEmpty={!isDefinedString(totalCommonAreaShareString)}
                          loading={isFetching}
                        >
                          {totalCommonAreaShareString}
                        </Text>
                      </th>
                      <th align="left" className="px-1 pt-2">
                        <Text
                          className="text-sm font-medium text-white"
                          isEmpty={!isDefinedString(totalCalculatedLettableAreaString)}
                          loading={isFetching}
                        >
                          {totalCalculatedLettableAreaString}
                        </Text>
                      </th>
                      <th align="left" className="px-1 pt-2">
                        <Text
                          className="text-sm font-medium text-white"
                          isEmpty={!isDefinedString(totalOverrideAreaString)}
                          loading={isFetching}
                        >
                          {totalOverrideAreaString}
                        </Text>
                      </th>
                      <th colSpan={5} align="right" className="pr-4 pl-1 pt-2 text-right">
                        <Text
                          className="text-sm font-medium text-white"
                          isEmpty={!isDefinedString(totalAmount)}
                          loading={isFetching}
                        >
                          {totalAmount}
                        </Text>
                      </th>
                    </tr>
                  </tfoot>
                )}
              </Table>

              {/* {!editing && <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />} */}
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />

      {editing && (
        <SpacesAddModal
          open={addingSpaces}
          duration={agreement?.duration}
          selected={spaces}
          onClose={handleCloseAddSpaces}
          onSubmit={handleAddSpaces}
          rootId={constrainedBuildingId}
          showHierarchy
        />
      )}
    </>
  );
}
